// Shared-data console: every live share in this company, in one list.
//
// The owner's ask: "one place that shows what is shared out of this
// company, with a way to extend it or end it." Two stores feed it - an
// Issue keeps its sharing state on the document, everything else hangs
// off a ResourceShare row - and the backend unions them so this page
// never has to care which.
//
// Super admin sees every company; a company admin sees their own and
// nothing else. That scoping is enforced in the route, not here
// (back/routes/adminShares.js), and the page shows which scope it is
// looking at rather than leaving an admin to assume.
//
// Backend: GET /admin/shares, PUT /admin/shares/:kind/:id/extend,
// PUT /admin/shares/:kind/:id/revoke.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import {
  Alert, Button, Card, DatePicker, Empty, Input, Modal, Popconfirm,
  Select, Skeleton, Table, Tag, Tooltip, message,
} from "antd";
import { url, tokenRef } from "api";
import {
  shareStatus, extendCaveat, expiryLabel, subjectLabel, recipientSummary,
  listState, usableList, emptyMessage, countsLine, failureDetail, FAILURE_TITLE,
  openedState, openedSummary, visitorLine, hasOpened,
  NOT_REQUIRED, UNRECORDED, INVITED_NOT_OPENED,
  STATUS_LABEL, STATUS_COLOUR, ACTIVE, REVOKED, EXPIRED, OFF, FAILED,
} from "./shareStatus";

async function api(endpoint, options = {}) {
  const token = localStorage.getItem(tokenRef);
  const res = await fetch(`${url}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  // A BODY WE CANNOT READ IS NOT AN EMPTY ANSWER. This used to be
  // `.json().catch(() => ({}))`, so a 200 carrying anything that is not JSON -
  // a proxy's HTML error page, a gateway notice, a truncated response - became
  // `{}`, then `rows: []`, then "0 active · 0 expired · 0 revoked · 0 turned
  // off" over "Nothing is shared out of this company", with loadError null and
  // nothing on screen admitting it had failed. That is the EXACT bug listState
  // was written to prevent, arriving through the one door it does not watch:
  // listState is only as good as the `error` it is handed.
  // Driven on staging 2026-10-01: a 500 and a dropped connection both said
  // "This list could not be loaded"; a 200 with an HTML body said nothing is
  // shared while 535 things were.
  let json;
  try {
    json = await res.json();
  } catch (e) {
    throw new Error(`the server answered ${res.status} with a body that is not readable`);
  }
  if (!res.ok) throw new Error(json.err || `HTTP ${res.status}`);
  return json;
}

export default function SharedDataPage() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [extending, setExtending] = useState(null);   // the row being extended
  const [newExpiry, setNewExpiry] = useState(null);
  const [busy, setBusy] = useState(false);
  // A FAILED LOAD AND AN EMPTY COMPANY ARE THE SAME SHAPE - zero rows - and
  // this page must never confuse them. See listState in ./shareStatus.
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("/admin/shares");
      // AND A 200 WITHOUT A LIST IS ALSO NOT AN EMPTY LIST. `res.rows || []`
      // cannot tell "no shares" from "this is not the answer I asked for".
      if (!usableList(res)) {
        throw new Error("the server's answer did not contain a list of shares");
      }
      setRows(res.rows);
      setScope(res.scope || "");
      setLoadError(null);
    } catch (e) {
      // The toast fades; the page must keep saying it does not know.
      setLoadError(e.message || "unknown error");
      setRows([]);
      setScope("");
      message.error(`Could not load shares: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (row) => {
    setBusy(true);
    try {
      await api(`/admin/shares/${row.kind}/${row.id}/revoke`, { method: "PUT" });
      message.success("Share ended");
      load();
    } catch (e) {
      message.error(`Could not end this share: ${e.message}`);
    } finally { setBusy(false); }
  };

  const saveExpiry = async () => {
    if (!extending) return;
    setBusy(true);
    try {
      const res = await api(`/admin/shares/${extending.kind}/${extending.id}/extend`, {
        method: "PUT",
        body: JSON.stringify({ expiresAt: newExpiry || null }),
      });
      // the route says so when the new date cannot take effect yet
      if (res.warning) message.warning(res.warning);
      else message.success(newExpiry ? "Expiry updated" : "Expiry removed - this share no longer lapses");
      setExtending(null); setNewExpiry(null);
      load();
    } catch (e) {
      message.error(`Could not change the expiry: ${e.message}`);
    } finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && shareStatus(r) !== statusFilter) return false;
      if (!q) return true;
      return [r.title, r.resourceType, r.shareToken, r.projectId, r.displayId]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { [ACTIVE]: 0, [EXPIRED]: 0, [REVOKED]: 0, [OFF]: 0 };
    rows.forEach((r) => { c[shareStatus(r)] += 1; });
    return c;
  }, [rows]);

  const state = listState({ loading, error: loadError, total: rows.length, shown: visible.length });

  // AFTER EVERY HOOK, NEVER BEFORE ONE. This started above the two
  // useMemos and changed the hook order on the render where the redirect
  // fired - eslint caught it, and it is the real thing rather than the
  // false positive this repo throws in large files.
  //
  // The route refuses anybody else regardless; this only avoids rendering a
  // page that would 403 on every request.
  if (userInfoRedux && !userInfoRedux.admin && !userInfoRedux.companyAdmin) {
    return <Redirect to="/dashboard" />;
  }

  const columns = [
    {
      title: "What is shared",
      key: "subject",
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>
            {subjectLabel(r)}
            {r.displayId ? <span style={{ color: "#6b7280" }}> · #{r.displayId}</span> : null}
          </div>
          {r.title ? (
            <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.title}
            </div>
          ) : null}
          <Tag style={{ fontSize: 10, marginTop: 2 }}>{r.kind === "issue" ? "issue document" : "link share"}</Tag>
        </div>
      ),
    },
    {
      // WHO WAS GIVEN IT - an admin added these people deliberately.
      title: "Who has it",
      key: "recipients",
      render: (_, r) => <span>{recipientSummary(r)}</span>,
    },
    {
      // WHO WALKED THROUGH IT - nobody granted these people anything; they
      // followed a link, possibly forwarded, and typed a name. That is why
      // the header is "Opened by" and not "Granted" or "Added": the same
      // provenance distinction as PARTNER / GUEST / UNKNOWN.
      title: "Opened by",
      key: "openedBy",
      width: 300,
      render: (_, r) => {
        const st = openedState(r);
        // amber for the three that are easy to misread as an all-clear:
        // a link that never asks who, one opened by nobody we can name, and
        // one that was only ever SENT.
        const tone = (st === NOT_REQUIRED || st === UNRECORDED || st === INVITED_NOT_OPENED)
          ? "#b45309" : "#6b7280";
        return (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: tone }}>{openedSummary(r)}</div>
            {(r.visitors || []).map((v, n) => (
              <div key={n} style={{
                fontSize: 12, marginTop: 2,
                textDecoration: v.revoked ? "line-through" : "none",
                // somebody who was only sent the link is not a visitor, and
                // must not read like one
                color: hasOpened(v) ? undefined : "#9ca3af",
              }}>
                {visitorLine(v)}
                {v.revoked ? <Tag style={{ fontSize: 10, marginLeft: 4 }}>access withdrawn</Tag> : null}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      filters: [ACTIVE, EXPIRED, REVOKED, OFF].map((s) => ({ text: STATUS_LABEL[s], value: s })),
      onFilter: (v, r) => shareStatus(r) === v,
      render: (_, r) => <Tag color={STATUS_COLOUR[shareStatus(r)]}>{STATUS_LABEL[shareStatus(r)]}</Tag>,
    },
    {
      title: "Expires",
      key: "expires",
      render: (_, r) => <span>{expiryLabel(r)}</span>,
    },
    {
      title: "Last opened",
      key: "last",
      render: (_, r) => (r.lastAccessedAt
        ? new Date(r.lastAccessedAt).toISOString().slice(0, 10)
        : <span style={{ color: "#9ca3af" }}>never</span>),
    },
    {
      title: "",
      key: "actions",
      width: 190,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Tooltip title={extendCaveat(r) || "Change when this share lapses"}>
            <Button size="small" onClick={() => { setExtending(r); setNewExpiry(r.expiresAt || null); }}>
              Extend
            </Button>
          </Tooltip>
          <Popconfirm
            title={r.kind === "issue"
              ? "Turn off this issue's public link? Its guests keep their individual access."
              : "End this share? Everybody holding the link loses access immediately."}
            okText="End it"
            onConfirm={() => revoke(r)}
            disabled={shareStatus(r) === REVOKED}
          >
            <Button size="small" danger disabled={shareStatus(r) === REVOKED || busy}>Remove</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title="Shared data"
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Input.Search allowClear placeholder="Search title, type or token"
              style={{ width: 260 }} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select value={statusFilter} style={{ width: 150 }} onChange={setStatusFilter}
              options={[{ value: "all", label: "All statuses" },
                ...[ACTIVE, EXPIRED, REVOKED, OFF].map((s) => ({ value: s, label: STATUS_LABEL[s] }))]} />
            <Button onClick={load} loading={loading}>Refresh</Button>
          </div>
        }
      >
        {state === FAILED ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={FAILURE_TITLE}
            description={failureDetail(loadError)}
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={scope === "all-companies"
              ? "You are seeing shares from every company."
              : "You are seeing your own company's shares only."}
            description={
              countsLine(state, {
                active: counts[ACTIVE], expired: counts[EXPIRED],
                revoked: counts[REVOKED], off: counts[OFF],
              })
              + " Ending a share cannot be undone from here - the link has to be created again."
            }
          />
        )}
        {loading ? <Skeleton active /> : (
          <Table
            rowKey={(r) => `${r.kind}:${r.id}`}
            dataSource={visible}
            columns={columns}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true }}
            locale={{ emptyText: <Empty description={emptyMessage(state, loadError)} /> }}
          />
        )}
      </Card>

      <Modal
        open={!!extending}
        title="Change when this share lapses"
        onCancel={() => { setExtending(null); setNewExpiry(null); }}
        onOk={saveExpiry}
        confirmLoading={busy}
        okText="Save"
      >
        {extending ? (
          <>
            <p style={{ marginBottom: 8 }}>
              {subjectLabel(extending)}{extending.title ? ` — ${extending.title}` : ""}
            </p>
            {extendCaveat(extending) ? (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={extendCaveat(extending)} />
            ) : null}
            {/* The picker is UNCONTROLLED and we keep the value as an ISO
                string. antd 5's DatePicker speaks dayjs, not moment, and
                every other page in this repo takes the object the picker
                hands back rather than constructing one - so nothing here
                needs a date library at all, and the quick buttons below
                cannot hand it the wrong kind of object. */}
            <DatePicker
              style={{ width: "100%" }}
              onChange={(d) => setNewExpiry(d ? d.toISOString() : null)}
              placeholder="Pick a date, or use a button below"
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              {[7, 30, 90].map((n) => (
                <Button key={n} size="small"
                  onClick={() => setNewExpiry(new Date(Date.now() + n * 864e5).toISOString())}>
                  +{n} days
                </Button>
              ))}
              <Button size="small" onClick={() => setNewExpiry(null)}>Never lapse</Button>
            </div>
            <p style={{ marginTop: 10, marginBottom: 0, color: "#6b7280" }}>
              {newExpiry
                ? `Will lapse on ${new Date(newExpiry).toISOString().slice(0, 10)}`
                : "Will never lapse"}
            </p>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
